import { AdvancedSelect } from '../../../widgets/select';
import { Columns } from '../../../widgets/columns';
import { FormControl } from '../../../widgets/formControl';
import { Icon } from '../../../widgets/icon';
import { Input } from '../../../widgets/input';
import { NotificationBanner } from '../../../widgets/notificationBanner';
import { showNotification } from '../../../widgets/notificationToaster';
import { ZTooltip } from '../../../widgets/ztooltip';
import _ from 'lodash';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { SingleValue } from 'react-select';
import { Separator } from 'src/shared/globals';
import { cartesianProduct, stringFormat, T, toGlobalID, toLocalID, uuid } from 'src/shared/helpers';
import AngleLeft from 'src/shared/icons/angle-left-solid.svg';
import AngleRight from 'src/shared/icons/angle-right-solid.svg';
import InfoCircleIcon from 'src/shared/icons/info-circle-solid.svg';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import { ProductColor } from '../state/model/modelColor';
import { MerchantProductOption, ModelAttribute, ModelColorMapping, ModelOption } from '../state/model/modelColorMapping';
import { ColorAttribute } from '../steps/4-variants/helpers';
import { PartialSalesChannel } from '../steps/6-done/connectModelDialog';

interface Mapping {
    ProductColorMapped?: ProductColor | null;
    ProductAttributeId?: string;
    MerchantAttributeId?: string;
    ProductAttributeLabel?: string;
    MerchantProductAttributeLabel?: string;
    Options?: {
        ProductOptionId: string;
        MerchantProductOptionId: string;
        ProductOptionLabel: string;
        MerchantProductOptionLabel: string;
    }[];
}

interface MappingSalesChannel {
    ProductSKU: string;
    SalesChannelID: string;
    SalesChannelRemoteUrl: string;
    MerchantProductName: string;
    Mappings: Mapping[];
}

const MappingColumns = styled(Columns)<{ numCols: number }>`
    grid-column-gap: 20px;
    grid-template-columns: 1fr repeat(${(props) => props.numCols}, 1fr);
`;

const AttributeSeparator = styled(Separator)<{ isLastSeparator: boolean }>`
    ${(p) => (p.isLastSeparator ? 'margin: 30px 0 0px !important' : 'margin: 10px 0px 28px !important')};
`;

const MappingTableContainer = styled.div`
    display: grid;
    padding-right: 25px;

    .ctrlVisible {
        display: auto;
    }

    .ctrlHidden {
        display: none;
    }

    .errorOption {
        border: 2px solid red;
        border-radius: 6px;
    }
`;

const HeaderColumnMappingContainer = styled.div`
    width: 100%;
`;

const HeaderColumn = styled.strong<{ numSalesChannels: number }>`
    display: block;
    font-size: 16px;
    text-overflow: ellipsis;
    overflow: hidden;
    margin-bottom: 10px;
    width: ${(props) => (props.numSalesChannels <= 2 ? '100%' : '250px')};

    @media screen and (max-width: 1266px) {
        width: ${(props) => (props.numSalesChannels == 1 ? '250px' : props.numSalesChannels <= 2 ? '200px' : '100px')};
    }
`;

const HeaderSecondaryColumn = styled.label`
    display: block;
    font-size: 13px;
    color: ${theme.app.textColor};
    margin: -5px 0 5px;
`;

const NoVariantsWarning = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin: 5px 0 10px;
    font-size: 12px;
    color: #d9534f;
    line-height: 16px;

    > svg {
        flex-shrink: 0;
        width: 14px;
        height: 14px;
        margin-top: 1px;
        fill: #d9534f;
    }
`;

const FromControlAttributeSC = styled(FormControl)<{ isForMappingProductWithoutVariant?: boolean }>`
    > div {
        ${(props) => props.isForMappingProductWithoutVariant === true && 'margin-top: -58px !important;'}
        width: 100%;
        position: relative;

        .tooltipArrow {
            cursor: pointer;
            position: absolute;
            width: 20px;

            > svg {
                font-size: 20px;
            }
        }

        > div {
            width: 100%;
        }
    }
`;

const LeftArrow = styled(ZTooltip)<{ isFirstAttribute: boolean }>`
    left: -23px;
    top: ${(props) => (props.isFirstAttribute ? '37px' : '10px')};
`;

const RightArrow = styled(ZTooltip)<{ isFirstAttribute: boolean }>`
    right: -23px;
    top: ${(props) => (props.isFirstAttribute ? '37px' : '10px')};
`;

const MasterSalesChannelAttributeName = styled.label`
    font-size: 14px;
    font-weight: bold;
    padding: 5px 0;
    display: flex;
    align-items: center;
    height: 40px;
`;

const SecondarySalesChannelAttributeName = styled(Input)`
    font-size: 14px;
    font-weight: bold;
`;

const MasterSalesChannelOptionValue = styled.label`
    font-size: 14px;
    padding: 5px 0 5px 20px;
    display: flex;
    align-items: center;
    height: 40px;
`;

const SecondarySalesChannelAttribute = styled(Input)`
    font-weight: bold;
    font-size: 14px;
    color: #000;
`;

const SelectCustomStyle = {
    singleValue: (base: any, state: any) => ({
        ...base,
        fontWeight: 'bold'
    })
};

const LabelErrorMessageOption = styled.label`
    color: red;
    font-size: 12px;
    margin: 5px 0 15px;
    display: block;
`;

// Parent page
const ConnectProductCatalogVariants: FunctionComponent<{
    forEditProduct: boolean;
    hasButtonPublishClicked?: boolean;
    isProductTemplate: boolean;
    productZakekeName: string;
    productSalesChannelUrl: string | null;
    productColorsToConnect: ProductColor[];
    salesChannels: PartialSalesChannel[];
    mappingsSet?: ModelColorMapping[];
    salesChannelID: string;
    additionalCodes: { code: string; salesChannelID: string; merchantProductName: string }[];
    merchantProductOptionsLoaded: MerchantProductOption[] | null | undefined;
    merchantProductSku: string;
    selectedMerchantProductName: string;
    cardTitle?: string;
    cardTitleHtml?: React.ReactNode;
    showHeader?: boolean;
    showBackButton?: boolean;
    hideAlreadyConfiguredBadge?: boolean;
    onConfirm?: () => void;
    onConfirmImporting?: (productMerchantOptions: MerchantProductOption[]) => void;
    onErrorImporting?: () => void;
    onCompleteLoadingMerchantAttributes?: () => void;
    onUpdateMapping?: (mappingsToSave: ModelColorMapping[]) => void;
    onlyImporting: boolean;
    enableMultiSalesChannels?: boolean;
    mainStoreVariantsRemoved?: boolean;
}> = ({
    forEditProduct,
    hasButtonPublishClicked,
    isProductTemplate,
    productZakekeName,
    productSalesChannelUrl,
    productColorsToConnect,
    salesChannels,
    mappingsSet,
    salesChannelID,
    additionalCodes,
    merchantProductOptionsLoaded,
    merchantProductSku,
    selectedMerchantProductName,
    onlyImporting,
    onConfirmImporting,
    onErrorImporting,
    onCompleteLoadingMerchantAttributes,
    onUpdateMapping,
    enableMultiSalesChannels,
    mainStoreVariantsRemoved
}) => {
    // Functions
    function createProductColorMapping(mappingsToSave: MappingSalesChannel[], productVariationsToConnect: ProductColor[]): ModelColorMapping[] {
        let productColorMappingForSalesChannels: ModelColorMapping[] = [];

        if (mappingsToSave.length > 0) {
            // Product variants with merchant code to map
            let productVariantsWithMerchantCode: ModelColorMapping[] = [];
            mappingsToSave.forEach((msc, i) => {
                // Product without variants
                if (msc.Mappings.length == 1 && msc.Mappings[0].ProductColorMapped) {
                    productVariantsWithMerchantCode = [
                        {
                            colorID: msc.Mappings[0].ProductColorMapped.colorID ?? -1,
                            salesChannelID: msc.SalesChannelID,
                            productSKU: msc.ProductSKU,
                            productColor: {
                                code: msc.Mappings[0].ProductColorMapped!.code,
                                colorCode: msc.Mappings[0].ProductColorMapped!.colorCode,
                                colorID: msc.Mappings[0].ProductColorMapped.colorID ?? -1,
                                guid: msc.Mappings[0].ProductColorMapped.guid,
                                name: msc.Mappings[0].ProductColorMapped!.name
                            },
                            merchantCode: '[]'
                        }
                    ];
                }

                // Product with variants
                else if (msc.Mappings[0].ProductAttributeId != undefined) {
                    let colorAttributesMerchantProduct = msc.Mappings.map((ms) =>
                        (ms.Options ?? []).map((mso) => {
                            return {
                                Id: ms.MerchantAttributeId,
                                Label: ms.MerchantProductAttributeLabel,
                                Value: {
                                    Id: mso.MerchantProductOptionId,
                                    Label: mso.MerchantProductOptionLabel
                                }
                            } as ColorAttribute;
                        })
                    );

                    if (colorAttributesMerchantProduct.length == 1) {
                        productVariantsWithMerchantCode = productVariationsToConnect.map((tpc, i) => {
                            return {
                                colorID: tpc.colorID ?? -1,
                                salesChannelID: msc.SalesChannelID,
                                productSKU: msc.ProductSKU,
                                productColor: {
                                    code: tpc.code,
                                    colorCode: tpc.colorCode,
                                    guid: tpc.guid,
                                    colorID: tpc.colorID ?? -1,
                                    name: tpc.name
                                },
                                merchantCode: JSON.stringify([colorAttributesMerchantProduct[0][i]])
                            } as ModelColorMapping;
                        });
                    } else {
                        let allMerchantProductVariants: ColorAttribute[][] = cartesianProduct(...colorAttributesMerchantProduct);

                        let allProductVariants: ColorAttribute[][] = cartesianProduct(...colorAttributesProduct);

                        productVariantsWithMerchantCode = productVariationsToConnect.map((variant, i) => {
                            return {
                                colorID: variant.colorID ?? -1,
                                salesChannelID: msc.SalesChannelID,
                                productSKU: msc.ProductSKU,
                                productColor: {
                                    code: variant.code,
                                    colorCode: variant.colorCode,
                                    guid: variant.guid,
                                    colorID: variant.colorID ?? -1,
                                    name: variant.name
                                },
                                merchantCode: getMerchantCode(allMerchantProductVariants, allProductVariants, variant)
                            } as ModelColorMapping;
                        });
                    }
                }

                productVariantsWithMerchantCode = productVariantsWithMerchantCode.filter(
                    (p) => p!.merchantCode.indexOf('No map') == -1 && (p.productColor != null || p.productColor == undefined)
                );
                productColorMappingForSalesChannels = productColorMappingForSalesChannels.concat(productVariantsWithMerchantCode);
            });
            return productColorMappingForSalesChannels;
        }
        return [];
    }

    function getMerchantCode(allMerchantVariants: ColorAttribute[][], allModelVariants: ColorAttribute[][], modelColor: ProductColor) {
        let numAttributesForVariant = (JSON.parse(modelColor.code || '[]') as ColorAttribute[]).length;
        let newAllMerchantVariants = allMerchantVariants;
        let newAllModelVariants = allModelVariants;

        if ((JSON.parse(modelColor.code || '[]') as ColorAttribute[]).length < allMerchantVariants[0].length) {
            newAllMerchantVariants = allMerchantVariants.map((p) => p.slice(0, numAttributesForVariant));
            newAllModelVariants = allModelVariants.map((p) => p.slice(0, numAttributesForVariant));
        }

        let indxFound = newAllModelVariants.map((m) => JSON.stringify(m)).findIndex((code) => code == modelColor.code);
        let merchantCode = newAllMerchantVariants.map((m) => JSON.stringify(m))[indxFound];
        return merchantCode;
    }

    function getMappingAttributes(
        forEditProduct: boolean,
        ma: ModelAttribute,
        mappings: ModelColorMapping[],
        merchantProductOptionsSC: MerchantProductOption[],
        indxAttribute: number
    ) {
        let retValues: any[] = [];

        let modelColorsCodes = productColors.map((c) => {
            return {
                colorID: c.colorID,
                guid: c.guid,
                code: (JSON.parse(c.code || '[]') as ColorAttribute[])[indxAttribute]
            };
        });

        let merchantCodeAttribute = mappings && mappings.length > 0 ? (JSON.parse(mappings[0].merchantCode || '[]') as ColorAttribute[])[indxAttribute] : null;
        let defaultAttribute =
            merchantProductOptionsSC.find(
                (m) => ma.Label.toLowerCase().startsWith(m.label.toLowerCase()) || m.label.toLowerCase().startsWith(ma.Label.toLowerCase())
            ) ?? merchantProductOptionsSC[indxAttribute];

        let newMappingAttribute = {
            ProductAttributeId: ma.Id,
            ProductAttributeLabel: ma.Label,
            MerchantAttributeId: merchantCodeAttribute?.Id ?? defaultAttribute.optionID,
            MerchantProductAttributeLabel: merchantCodeAttribute?.Label ?? defaultAttribute.label,
            Options: ma.Values.map((o, j) => {
                let modelColorIds = modelColorsCodes.filter((m) => m.code.Value.Id == o.Id).map((m) => m.colorID);
                let modelColorGuids = modelColorsCodes.filter((m) => m.code.Value.Id == o.Id).map((m) => m.guid);

                let option =
                    mappings && mappings.length > 0
                        ? (
                              JSON.parse(
                                  mappings.find(
                                      (m) =>
                                          modelColorIds.includes(m.colorID) ||
                                          (m.productColor?.guid !== undefined && modelColorGuids.includes(m.productColor?.guid ?? ''))
                                  )?.merchantCode ?? '[]'
                              ) as ColorAttribute[]
                          )[indxAttribute]
                        : null;
                let defaultOption = forEditProduct
                    ? { label: 'No map', valueID: '' }
                    : defaultAttribute.values.find((v) => v.label.toLowerCase().startsWith(o.Label.toLowerCase())) ?? {
                          label: 'No map',
                          valueID: ''
                      };

                return {
                    ProductOptionId: o.Id,
                    ProductOptionLabel: o.Label,
                    MerchantProductOptionLabel: option ? option.Value.Label : defaultOption?.label ?? 'No map',
                    MerchantProductOptionId: option ? option.Value.Id : defaultOption?.valueID ?? ''
                };
            })
        };

        return newMappingAttribute;
    }

    // Data
    const modelAttributes: ModelAttribute[] = [];
    const merchantProductOptions = merchantProductOptionsLoaded || [];
    const originalModelColors = productColorsToConnect.length > 0 ? productColorsToConnect.map((c) => JSON.parse(c.code || '[]') as ColorAttribute[]) : [];
    const optionsAttributesForProduct =
        originalModelColors.length > 0
            ? originalModelColors[0].map((attribute) => ({
                  label: attribute.Label,
                  value: attribute.Id
              }))
            : [];
    const groupMOpt = _.groupBy(
        merchantProductOptions.sort((mp1, mp2) => mp1.salesChannelID - mp2.salesChannelID),
        (item) => `${item.salesChannelID}_${item.sku}`
    );
    const keys = Object.keys(groupMOpt);
    const merchantProductOptionsGrouped = keys.map((k) => {
        return { salesChannelID: toLocalID(k.split('_')[0]), sku: k.split('_')[1], values: groupMOpt[k] };
    });

    // State
    const [modelColors, setModelColors] = useState<ColorAttribute[][]>(
        merchantProductOptions.length > 0 &&
            originalModelColors.length > 0 &&
            merchantProductOptions.length < originalModelColors[0].length &&
            isProductTemplate
            ? _.uniqBy(
                  originalModelColors.map((m) => m[0]),
                  (obj) => obj.Value.Id
              ).map((m) => [m])
            : originalModelColors
    );
    const [productColors, setProductColors] = useState<ProductColor[]>(
        merchantProductOptions.length > 0 &&
            originalModelColors.length > 0 &&
            merchantProductOptions.length < originalModelColors[0].length &&
            isProductTemplate
            ? modelColors.map((mc) => {
                  return {
                      code: JSON.stringify(mc),
                      colorCode: JSON.stringify(mc),
                      colorID: -1,
                      name: '',
                      guid: uuid()
                  };
              })
            : productColorsToConnect
    );

    const maxNumAttributes = Math.max(...modelColors.map((m) => m.length));

    if (modelColors.length > 0) {
        for (let i = 0; i < maxNumAttributes; i++) {
            let modelColorWithAttributes = modelColors.find((m) => m.length == maxNumAttributes)!;
            modelAttributes.push({
                Id: modelColorWithAttributes[i].Id,
                Label: modelColorWithAttributes[i].Label,
                Values: []
            });
            modelColors.forEach((m) => {
                if (m.length > i) {
                    modelAttributes[i].Values.push({
                        Id: m[i].Value.Id,
                        Label: m[i].Value.Label
                    });
                }
            });
            modelAttributes[i].Values = [...new Set(modelAttributes[i].Values.map((o) => JSON.stringify(o)))].map((o2) => JSON.parse(o2));
        }
    }

    const colorAttributesProduct = modelAttributes.map((ms) =>
        (ms.Values ?? []).map((mso) => {
            return {
                Id: ms.Id,
                Label: ms.Label,
                Value: {
                    Id: mso.Id,
                    Label: mso.Label
                }
            } as ColorAttribute;
        })
    );

    const productVariationsOptions = productColors.map((pc) => {
        return {
            value: pc.colorID > 0 ? pc.colorID.toString() : pc.guid,
            label: pc.name
        };
    });

    const [mappingsSalesChannels, setMappingsSalesChannels] = useState<MappingSalesChannel[]>(
        merchantProductOptions.length == 0
            ? []
            : merchantProductOptionsGrouped.map((group, index) => {
                  let mappingSetSC = (mappingsSet?.filter((ms) => toLocalID(ms.salesChannelID) != toLocalID(salesChannelID) || forEditProduct) ?? []).filter(
                      (ms) => toLocalID(ms.salesChannelID) == group.salesChannelID
                  );
                  let merchantMappedCodes = mappingSetSC.map((m) => JSON.parse(m.merchantCode || '[]') as ColorAttribute[]);
                  return {
                      ProductSKU: group.sku,
                      SalesChannelID: toGlobalID(group.salesChannelID ?? '-1', 'MappingSalesChannel'),
                      SalesChannelRemoteUrl:
                          salesChannels.find((s) => toLocalID(s.id) == group.salesChannelID)?.ecommerceSettings?.remoteUrl ?? `Store ${index + 1}`,
                      MerchantProductName:
                          additionalCodes.find((ac) => toLocalID(ac.salesChannelID) == group.salesChannelID)?.merchantProductName ??
                          selectedMerchantProductName ??
                          '',
                      Mappings:
                          group.values.length == 1 && group.values[0].values.length == 0
                              ? // Mapping di un prodotto senza varianti
                                [
                                    {
                                        ProductColorMapped:
                                            mappingSetSC.length > 0
                                                ? productColors.find(
                                                      (pc) =>
                                                          pc.colorID == mappingSetSC[0].colorID ||
                                                          (pc.guid !== undefined && pc.guid == mappingSetSC[0].productColor?.guid)
                                                  ) ?? null
                                                : null
                                    }
                                ]
                              : // Mapping di un prodotto con varianti
                                modelAttributes.map((ma, i) => {
                                    let merchantProductOptionsSC = merchantProductOptions.filter((mpo) => mpo.salesChannelID == group.salesChannelID);
                                    return merchantProductOptionsSC.length > i
                                        ? getMappingAttributes(forEditProduct, ma, mappingSetSC, merchantProductOptionsSC, i)
                                        : ([] as Mapping);
                                })
                  };
              })
    );
    const [variationSelected, setVariationSelected] = useState<{ value: string; label: string } | null>(
        productVariationsOptions.length > 0
            ? mappingsSalesChannels.length > 0 &&
              mappingsSalesChannels[0].Mappings.length > 0 &&
              mappingsSalesChannels[0].Mappings[0].ProductColorMapped?.colorID
                ? productVariationsOptions.find((pv) => pv.value == mappingsSalesChannels[0].Mappings[0].ProductColorMapped?.colorID.toString()) ??
                  productVariationsOptions[0]
                : productVariationsOptions[0]
            : null
    );
    const [minIndexColumnVisible, setMinIndexColumnVisible] = useState(0);
    const [maxIndexColumnVisible, setMaxIndexColumnVisible] = useState(3);

    // Callbacks
    const onChangeProductAttribute = (item: SingleValue<{ value: string; label: string }>) => {
        let indx = originalModelColors[0].findIndex((m) => m.Id === item?.value);
        let newModelColors = [...modelColors];
        let newProductColors = [...productColors];

        newModelColors = _.uniqBy(
            originalModelColors.map((m) => m[indx]),
            (obj) => obj.Value.Id
        ).map((m) => [m]);
        newProductColors = newModelColors.map((mc) => ({
            code: JSON.stringify(mc),
            colorCode: JSON.stringify(mc),
            colorID: -1,
            name: '',
            guid: uuid()
        }));

        let maxNumAttributes = Math.max(...newModelColors.map((m) => m.length));
        let newModelAttributes: ModelAttribute[] = [];

        if (newModelColors.length > 0) {
            for (let i = 0; i < maxNumAttributes; i++) {
                let modelColorWithAttributes = newModelColors.find((m) => m.length == maxNumAttributes)!;
                newModelAttributes.push({
                    Id: modelColorWithAttributes[i].Id,
                    Label: modelColorWithAttributes[i].Label,
                    Values: []
                });
                newModelColors.forEach((m) => {
                    if (m.length > i) {
                        newModelAttributes[i].Values.push({
                            Id: m[i].Value.Id,
                            Label: m[i].Value.Label
                        });
                    }
                });
                newModelAttributes[i].Values = [...new Set(newModelAttributes[i].Values.map((o) => JSON.stringify(o)))].map((o2) => JSON.parse(o2));
            }
        }

        let newMappingSalesChannels = [...mappingsSalesChannels];
        newMappingSalesChannels = merchantProductOptionsGrouped.map((group, index) => {
            let mappingSetSC = (mappingsSet?.filter((ms) => toLocalID(ms.salesChannelID) != toLocalID(salesChannelID)) ?? []).filter(
                (ms) => toLocalID(ms.salesChannelID) == group.salesChannelID
            );
            let merchantMappedCodes = mappingSetSC.map((m) => JSON.parse(m.merchantCode || '[]') as ColorAttribute[]);
            return {
                ProductSKU: group.sku,
                SalesChannelID: toGlobalID(group.salesChannelID ?? '-1', 'MappingSalesChannel'),
                SalesChannelRemoteUrl: salesChannels.find((s) => toLocalID(s.id) == group.salesChannelID)?.ecommerceSettings?.remoteUrl ?? `Store ${index + 1}`,
                MerchantProductName:
                    additionalCodes.find((ac) => toLocalID(ac.salesChannelID) == group.salesChannelID)?.merchantProductName ??
                    selectedMerchantProductName ??
                    '',
                Mappings:
                    group.values.length == 1 && group.values[0].values.length == 0
                        ? // Mapping di un prodotto senza varianti
                          [
                              {
                                  ProductColorMapped:
                                      mappingSetSC.length > 0
                                          ? newProductColors.find(
                                                (pc) =>
                                                    pc.colorID == mappingSetSC[0].colorID ||
                                                    (pc.guid !== undefined && pc.guid == mappingSetSC[0].productColor?.guid)
                                            ) ?? null
                                          : null
                              }
                          ]
                        : // Mapping di un prodotto con varianti
                          newModelAttributes.map((ma, i) => {
                              let merchantProductOptionsSC = merchantProductOptions.filter((mpo) => mpo.salesChannelID == group.salesChannelID);
                              return merchantProductOptionsSC.length > i
                                  ? getMappingAttributes(forEditProduct, ma, mappingSetSC, merchantProductOptionsSC, i)
                                  : ([] as Mapping);
                          })
            };
        });

        onUpdateMapping && onUpdateMapping(createProductColorMapping(newMappingSalesChannels, newProductColors));

        setModelColors(newModelColors);
        setProductColors(newProductColors);
        setMappingsSalesChannels(newMappingSalesChannels);
    };

    const onChangeAttribute = (
        item: SingleValue<{
            value: string;
            label: string;
        }>,
        productAttribute: ModelAttribute,
        salesChannelID: string
    ) => {
        let newMappingsSC = [...mappingsSalesChannels];
        let newAttributeMapping = newMappingsSC
            .find((nmsc) => toLocalID(nmsc.SalesChannelID) === toLocalID(salesChannelID))
            ?.Mappings.find((m) => m.ProductAttributeId == productAttribute.Id);
        let newMappings = newMappingsSC.find((nmsc) => toLocalID(nmsc.SalesChannelID) === toLocalID(salesChannelID))?.Mappings ?? [];

        if (newAttributeMapping) {
            newAttributeMapping.MerchantAttributeId = item?.value!;
            newAttributeMapping.MerchantProductAttributeLabel = item?.label!;
            let newMappingOptions = [...(newAttributeMapping.Options ?? [])].map((o, i) => {
                let newMerchantAttribute = merchantProductOptions.find((mo) => mo.optionID === item?.value)!;
                let defaultOption = forEditProduct
                    ? { label: 'No map', valueID: '' }
                    : newMerchantAttribute.values.length > i
                    ? newMerchantAttribute.values[i]
                    : newMerchantAttribute.values[0];
                let newMerchantOption: { label: string; valueID: string } =
                    newMerchantAttribute.values.find((mo) => mo.label.toLowerCase().startsWith(o.ProductOptionLabel.toLowerCase())) || defaultOption;
                return {
                    ProductOptionId: o.ProductOptionId,
                    ProductOptionLabel: o.ProductOptionLabel,
                    MerchantProductOptionId: newMerchantOption.valueID,
                    MerchantProductOptionLabel: newMerchantOption.label
                };
            });

            newAttributeMapping.Options = newMappingOptions;

            if ([...new Set(newMappings.map((ms) => ms.MerchantAttributeId))].length != newMappings.map((ms) => ms.ProductAttributeId).length) {
                onUpdateMapping && onUpdateMapping([]);
                return;
            }

            setMappingsSalesChannels(newMappingsSC);
            onUpdateMapping && onUpdateMapping(createProductColorMapping(newMappingsSC, productColors));
        }
    };

    const onChangeOption = (
        item: SingleValue<{ value: string; label: string }>,
        productAttribute: ModelAttribute,
        productOption: ModelOption,
        salesChannelID: string
    ) => {
        let newMappingsSC = [...mappingsSalesChannels];
        let newMappings = newMappingsSC.find((nmsc) => toLocalID(nmsc.SalesChannelID) === toLocalID(salesChannelID))?.Mappings ?? [];
        let newAttributeMapping = newMappingsSC
            .find((nmsc) => toLocalID(nmsc.SalesChannelID) === toLocalID(salesChannelID))
            ?.Mappings.find((m) => m.ProductAttributeId == productAttribute.Id);

        if (newAttributeMapping) {
            let newOptionMapping = newAttributeMapping.Options?.find((o) => o.ProductOptionId == productOption.Id);
            if (newOptionMapping) {
                newOptionMapping.MerchantProductOptionId = item?.value!;
                newOptionMapping.MerchantProductOptionLabel = item?.label!;
            }

            if ([...new Set(newMappings.map((ms) => ms.MerchantAttributeId))].length != newMappings.map((ms) => ms.ProductAttributeId).length) {
                onUpdateMapping && onUpdateMapping([]);
                return;
            }

            setMappingsSalesChannels(newMappingsSC);

            let mappingForProductColors = createProductColorMapping(mappingsSalesChannels, productColors);
            onUpdateMapping && onUpdateMapping(mappingForProductColors);
        }
    };

    const onChangeMainProductVariation = (
        item: SingleValue<{
            value: string;
            label: string;
        }>,
        salesChannelID: string,
        productSKU: string
    ) => {
        setVariationSelected(item);

        let newMappingsSC = [...mappingsSalesChannels];
        let newMappings =
            newMappingsSC.find((msc) => toLocalID(msc.SalesChannelID) == toLocalID(salesChannelID) && msc.ProductSKU == productSKU)?.Mappings ??
            newMappingsSC.find((msc) => msc.ProductSKU == productSKU)?.Mappings ??
            [];
        if (newMappings.length > 0) {
            newMappings[0].ProductColorMapped = productColors.find((pc) => pc.guid == item?.value || pc.colorID.toString() == (item?.value ?? -1).toString());
        }

        setMappingsSalesChannels(newMappingsSC);
        onUpdateMapping && onUpdateMapping(createProductColorMapping(newMappingsSC, productColors));
    };

    const onChangeMainProductVariationForAttribute = (
        option: SingleValue<{ value: string; label: string }>,
        attribute: ModelAttribute,
        salesChannelID: string,
        productSKU: string
    ) => {
        let variantMapping = mappingsSalesChannels.find((msc) => msc.SalesChannelID == salesChannelID)?.Mappings[0].ProductColorMapped;
        let newVariationSelected = variantMapping
            ? { value: variantMapping.guid ?? variantMapping.colorID.toString(), label: variantMapping.name }
            : { ...variationSelected };
        let modelColorSelected = productColors.find(
            (pc) => pc.guid == newVariationSelected.value || pc.colorID.toString() == (newVariationSelected.value ?? -1).toString()
        );
        let colorAttributes = JSON.parse(modelColorSelected?.code || '[]') as ColorAttribute[];
        let newAttribute = colorAttributes.find((ca) => ca.Id === attribute.Id)!;
        let newColorAttributeSelected = {
            ...newAttribute,
            Value: { Id: option?.value ?? '', Label: option?.label ?? '' }
        } as ColorAttribute;

        let index = colorAttributes.findIndex((c) => c.Id == attribute.Id);
        colorAttributes[index] = newColorAttributeSelected;

        let newColorCode = JSON.stringify(colorAttributes);
        let newVariant = productColors.find((pc) => pc.code == newColorCode);

        if (!newVariant) {
            showNotification({
                type: 'danger',
                message: (
                    <>
                        {stringFormat(T._('Unable to set variant "{0}": it does not configured', 'Admin'), colorAttributes.map((m) => m.Value.Label).join(' '))}
                    </>
                ),
                dismiss: {
                    duration: 3000
                }
            });
            return;
        }

        let item = { value: newVariant.guid ?? newVariant.colorID.toString(), label: newVariant.name };
        setVariationSelected(item);

        let newMappingsSC = [...mappingsSalesChannels];
        let newMappings =
            newMappingsSC.find((msc) => toLocalID(msc.SalesChannelID) == toLocalID(salesChannelID) && msc.ProductSKU == productSKU)?.Mappings ?? [];
        newMappings[0].ProductColorMapped = productColors.find((pc) => pc.guid == item.value || pc.colorID.toString() == (item.value ?? -1).toString());

        setMappingsSalesChannels(newMappingsSC);
        onUpdateMapping && onUpdateMapping(createProductColorMapping(newMappingsSC, productColors));
    };

    const onLeftSliderClick = () => {
        if (minIndexColumnVisible > 0) setMinIndexColumnVisible(minIndexColumnVisible - 1);

        if (maxIndexColumnVisible > 0) setMaxIndexColumnVisible(maxIndexColumnVisible - 1);
    };

    const onRightSliderClick = () => {
        if (maxIndexColumnVisible < mappingsSalesChannels.length) {
            setMinIndexColumnVisible(minIndexColumnVisible + 1);
            setMaxIndexColumnVisible(maxIndexColumnVisible + 1);
        }
    };

    // Hooks
    useEffect(() => {
        if (onlyImporting) {
            if (!merchantProductOptions || merchantProductOptions.length == 0) {
                showNotification({
                    type: 'danger',
                    message: <>{T._('Unable to import: product without variants.', 'Admin')}</>,
                    dismiss: {
                        duration: 3000
                    }
                });
                onErrorImporting && onErrorImporting();
            } else onConfirmImporting && onConfirmImporting(merchantProductOptions as MerchantProductOption[]);
        } else {
            if (productColors.length == 0) return;

            if (
                !isProductTemplate &&
                modelColors.length > 0 &&
                merchantProductOptions.filter((mpo) => mpo.optionID != '').length > 0 &&
                merchantProductOptions.filter((mpo) => mpo.optionID != '').length < modelColors[0].length
            ) {
                showNotification({
                    type: 'danger',
                    message: <>{T._('Unable to mapping attributes: merchant product attributes number less than target product attribute number.', 'Admin')}</>,
                    dismiss: {
                        duration: 3000
                    }
                });
                onErrorImporting && onErrorImporting();
            } else if (merchantProductOptions.length == 0 || (merchantProductOptions.length == 1 && merchantProductOptions[0].optionID == '')) {
                onUpdateMapping &&
                    onUpdateMapping([
                        {
                            colorID: productColors[0].colorID > 0 ? productColors[0].colorID : -1,
                            productSKU: merchantProductSku,
                            salesChannelID,
                            productColor: productColors[0],
                            merchantCode: '[]'
                        }
                    ]);
            } else {
                let mappingForProductColors = createProductColorMapping(mappingsSalesChannels, productColors);
                onUpdateMapping && onUpdateMapping(mappingForProductColors);
            }

            onCompleteLoadingMerchantAttributes && onCompleteLoadingMerchantAttributes();
        }
    }, []);

    // Render
    return onlyImporting ? null : (
        <React.Fragment>
            {merchantProductOptions.length == 0 || (merchantProductOptions.length == 1 && merchantProductOptions[0].optionID == '') ? (
                <>
                    {productVariationsOptions.length > 1 && (
                        <NotificationBanner
                            type='WARNING'
                            title={T._('No variants detected on this sales channel', 'Admin')}
                            content={T._(
                                'It looks like the product on this sales channel has only one variant or none at all. You can choose which of your configured variants to map it to. If you expect more variants, please check the product on your store before connecting it and try again.',
                                'Admin'
                            )}
                        />
                    )}
                    <FormControl label={T._('Choose variant to map', 'Admin')}>
                        <AdvancedSelect
                            isSearchable={false}
                            options={productVariationsOptions}
                            value={variationSelected ?? productVariationsOptions[0]}
                            menuPosition={'fixed'}
                            onChange={(item) => onChangeMainProductVariation(item, salesChannelID, merchantProductSku)}
                        ></AdvancedSelect>
                    </FormControl>
                </>
            ) : (
                <React.Fragment>
                    <MappingTableContainer>
                        {modelAttributes.map((ma, indexAttribute) => {
                            return (
                                <React.Fragment key={ma.Id}>
                                    {indexAttribute == 0 && (
                                        <MappingColumns numCols={mappingsSalesChannels.length <= 3 ? mappingsSalesChannels.length : 3}>
                                            {isProductTemplate &&
                                            !forEditProduct &&
                                            merchantProductOptions.length > 0 &&
                                            merchantProductOptions[0].values.length > 0 ? null : (
                                                <FormControl>
                                                    <HeaderColumnMapping
                                                        showTooltipOnPrimaryText={false}
                                                        numSalesChannel={mappingsSalesChannels.length}
                                                        primaryText={productZakekeName}
                                                        secondaryText={
                                                            isProductTemplate
                                                                ? T._('Product template', 'Admin')
                                                                : productSalesChannelUrl ?? T._('Product from scratch', 'Admin')
                                                        }
                                                        hasNoVariants={mainStoreVariantsRemoved}
                                                    />
                                                </FormControl>
                                            )}

                                            {mappingsSalesChannels.map((msc, indexAttributeSC) => {
                                                if (msc.Mappings.length == 1 && msc.Mappings[0].ProductColorMapped !== undefined) {
                                                    return (
                                                        <FromControlAttributeSC
                                                            key={'Attribute SC_' + msc.SalesChannelID + '_' + indexAttributeSC}
                                                            className={
                                                                indexAttributeSC < maxIndexColumnVisible && indexAttributeSC >= minIndexColumnVisible
                                                                    ? 'ctrlVisible'
                                                                    : 'ctrlHidden'
                                                            }
                                                        >
                                                            <HeaderColumnMapping
                                                                showTooltipOnPrimaryText={false}
                                                                numSalesChannel={mappingsSalesChannels.length}
                                                                primaryText={msc.MerchantProductName}
                                                                secondaryText={msc.SalesChannelRemoteUrl}
                                                                hasNoVariants={true}
                                                            />
                                                        </FromControlAttributeSC>
                                                    );
                                                } else {
                                                    let mappingAttributeForSC = msc.Mappings.find((m) => m.ProductAttributeId == ma.Id)!;
                                                    return (
                                                        mappingAttributeForSC && (
                                                            <FromControlAttributeSC
                                                                key={'Attribute SC_' + msc.SalesChannelID + '_' + indexAttributeSC}
                                                                className={
                                                                    indexAttributeSC < maxIndexColumnVisible && indexAttributeSC >= minIndexColumnVisible
                                                                        ? 'ctrlVisible'
                                                                        : 'ctrlHidden'
                                                                }
                                                            >
                                                                <HeaderColumnMapping
                                                                    showTooltipOnPrimaryText={false}
                                                                    numSalesChannel={mappingsSalesChannels.length}
                                                                    primaryText={msc.MerchantProductName}
                                                                    secondaryText={msc.SalesChannelRemoteUrl}
                                                                />
                                                            </FromControlAttributeSC>
                                                        )
                                                    );
                                                }
                                            })}

                                            {isProductTemplate &&
                                            !forEditProduct &&
                                            merchantProductOptions.length > 0 &&
                                            merchantProductOptions[0].values.length > 0 ? (
                                                <FormControl>
                                                    <HeaderColumnMapping
                                                        showTooltipOnPrimaryText={false}
                                                        numSalesChannel={mappingsSalesChannels.length}
                                                        primaryText={productZakekeName}
                                                        secondaryText={
                                                            isProductTemplate
                                                                ? T._('Product template', 'Admin')
                                                                : productSalesChannelUrl ?? T._('Product from scratch', 'Admin')
                                                        }
                                                        hasNoVariants={mainStoreVariantsRemoved}
                                                    />
                                                </FormControl>
                                            ) : null}
                                        </MappingColumns>
                                    )}

                                    <MappingColumns numCols={mappingsSalesChannels.length <= 3 ? mappingsSalesChannels.length : 3}>
                                        {merchantProductOptions.length > 0 ? (
                                            <FormControl>
                                                {productColorsToConnect.length != productColors.length ? (
                                                    <AdvancedSelect
                                                        isSearchable={false}
                                                        options={optionsAttributesForProduct}
                                                        menuPosition={'fixed'}
                                                        styles={SelectCustomStyle}
                                                        value={{
                                                            label: modelColors[0][0].Label,
                                                            value: modelColors[0][0].Id
                                                        }}
                                                        onChange={(item) => onChangeProductAttribute(item)}
                                                    ></AdvancedSelect>
                                                ) : (
                                                    <MasterSalesChannelAttributeName>{ma.Label}</MasterSalesChannelAttributeName>
                                                )}
                                            </FormControl>
                                        ) : (
                                            <React.Fragment></React.Fragment>
                                        )}

                                        {mappingsSalesChannels.map((msc, indexAttributeSC) => {
                                            if (msc.Mappings.length == 1 && msc.Mappings[0].ProductColorMapped !== undefined) {
                                                return (
                                                    <FromControlAttributeSC
                                                        key={'Attribute SC_' + msc.SalesChannelID + '_' + indexAttributeSC}
                                                        className={
                                                            indexAttributeSC < maxIndexColumnVisible && indexAttributeSC >= minIndexColumnVisible
                                                                ? 'ctrlVisible'
                                                                : 'ctrlHidden'
                                                        }
                                                        isForMappingProductWithoutVariant={true}
                                                    >
                                                        {mappingsSalesChannels.length > 3 &&
                                                            indexAttributeSC == minIndexColumnVisible &&
                                                            indexAttributeSC != 0 && (
                                                                <LeftArrow
                                                                    title={T._('Previous sales channel', 'Admin')}
                                                                    isFirstAttribute={indexAttribute == 0}
                                                                >
                                                                    <Icon $hoverable onClick={onLeftSliderClick}>
                                                                        <AngleLeft />
                                                                    </Icon>
                                                                </LeftArrow>
                                                            )}
                                                        {mappingsSalesChannels.length > 3 &&
                                                            indexAttributeSC == maxIndexColumnVisible - 1 &&
                                                            indexAttributeSC != mappingsSalesChannels.length - 1 && (
                                                                <RightArrow title={T._('Next sales channel', 'Admin')} isFirstAttribute={indexAttribute == 0}>
                                                                    <Icon $hoverable onClick={onRightSliderClick}>
                                                                        <AngleRight />
                                                                    </Icon>
                                                                </RightArrow>
                                                            )}
                                                    </FromControlAttributeSC>
                                                );
                                            } else {
                                                let attributesForSC =
                                                    merchantProductOptions
                                                        .filter((mpo) => mpo.salesChannelID == toLocalID(msc.SalesChannelID))
                                                        .map((merchantAttribute) => ({
                                                            value: merchantAttribute.optionID,
                                                            label: merchantAttribute.label
                                                        })) ?? [];

                                                let mappingAttributeForSC = msc.Mappings.find((m) => m.ProductAttributeId == ma.Id)!;
                                                return (
                                                    mappingAttributeForSC && (
                                                        <FromControlAttributeSC
                                                            key={'Attribute SC_' + msc.SalesChannelID + '_' + indexAttributeSC}
                                                            className={
                                                                indexAttributeSC < maxIndexColumnVisible && indexAttributeSC >= minIndexColumnVisible
                                                                    ? 'ctrlVisible'
                                                                    : 'ctrlHidden'
                                                            }
                                                        >
                                                            {mappingsSalesChannels.length > 3 &&
                                                                indexAttributeSC == minIndexColumnVisible &&
                                                                indexAttributeSC != 0 && (
                                                                    <LeftArrow
                                                                        title={T._('Previous sales channel', 'Admin')}
                                                                        isFirstAttribute={indexAttribute == 0}
                                                                    >
                                                                        <Icon $hoverable onClick={onLeftSliderClick}>
                                                                            <AngleLeft />
                                                                        </Icon>
                                                                    </LeftArrow>
                                                                )}
                                                            {mappingsSalesChannels.length > 3 &&
                                                                indexAttributeSC == maxIndexColumnVisible - 1 &&
                                                                indexAttributeSC != mappingsSalesChannels.length - 1 && (
                                                                    <RightArrow
                                                                        title={T._('Next sales channel', 'Admin')}
                                                                        isFirstAttribute={indexAttribute == 0}
                                                                    >
                                                                        <Icon $hoverable onClick={onRightSliderClick}>
                                                                            <AngleRight />
                                                                        </Icon>
                                                                    </RightArrow>
                                                                )}
                                                            {attributesForSC.length > 1 ? (
                                                                <AdvancedSelect
                                                                    isSearchable={false}
                                                                    options={attributesForSC}
                                                                    menuPosition={'fixed'}
                                                                    styles={SelectCustomStyle}
                                                                    value={attributesForSC.find((a) => a.value === mappingAttributeForSC.MerchantAttributeId)}
                                                                    onChange={(item) => onChangeAttribute(item, ma, msc.SalesChannelID)}
                                                                />
                                                            ) : (
                                                                <SecondarySalesChannelAttribute
                                                                    readOnly={true}
                                                                    value={
                                                                        attributesForSC.find((a) => a.value === mappingAttributeForSC.MerchantAttributeId)
                                                                            ?.label
                                                                    }
                                                                ></SecondarySalesChannelAttribute>
                                                            )}
                                                        </FromControlAttributeSC>
                                                    )
                                                );
                                            }
                                        })}
                                    </MappingColumns>

                                    {ma.Values.map((option, j) => {
                                        return (
                                            <MappingColumns key={option.Id} numCols={mappingsSalesChannels.length < 3 ? mappingsSalesChannels.length : 3}>
                                                {merchantProductOptions.length > 0 ? (
                                                    <FormControl>
                                                        <MasterSalesChannelOptionValue>{option.Label}</MasterSalesChannelOptionValue>
                                                    </FormControl>
                                                ) : (
                                                    <FormControl></FormControl>
                                                )}
                                                {mappingsSalesChannels.map((msc: MappingSalesChannel, indexOptionSC) => {
                                                    if (msc.Mappings.length == 1 && msc.Mappings[0].ProductColorMapped !== undefined) {
                                                        let variantSelectedMapping = productVariationsOptions.find(
                                                            (o) =>
                                                                o.value == msc.Mappings[0].ProductColorMapped?.guid ||
                                                                o.value == (msc.Mappings[0].ProductColorMapped?.colorID ?? -1).toString()
                                                        );
                                                        return indexAttribute == 0 && j == 0 ? (
                                                            <FromControlAttributeSC isForMappingProductWithoutVariant={true}>
                                                                <AdvancedSelect
                                                                    isSearchable={false}
                                                                    options={productVariationsOptions}
                                                                    value={variantSelectedMapping}
                                                                    menuPosition={'fixed'}
                                                                    placeholder={T._('Choose variant to map', 'Admin')}
                                                                    onChange={(item) => onChangeMainProductVariation(item, msc.SalesChannelID, msc.ProductSKU)}
                                                                ></AdvancedSelect>
                                                            </FromControlAttributeSC>
                                                        ) : (
                                                            <FormControl></FormControl>
                                                        );
                                                    }

                                                    let mappingAttributeForSC = msc.Mappings.find((m) => m.ProductAttributeId == ma.Id) ?? null;
                                                    let optionsForMerchantProductAttribute = merchantProductOptions.filter(
                                                        (mpo) => mpo.salesChannelID == toLocalID(msc.SalesChannelID)
                                                    );

                                                    let options = mappingAttributeForSC
                                                        ? optionsForMerchantProductAttribute
                                                              .find((mo) => mo.optionID == mappingAttributeForSC?.MerchantAttributeId)
                                                              ?.values!.map((mopt) => ({
                                                                  value: mopt.valueID,
                                                                  label: mopt.label
                                                              }))!
                                                        : [];

                                                    let optionsCombo = [{ value: '', label: 'No map' }, ...options];

                                                    let valueAttribute = optionsCombo.find((mopt) =>
                                                        mappingAttributeForSC?.Options?.find(
                                                            (mappOption) =>
                                                                mappOption.MerchantProductOptionId === mopt.value && mappOption.ProductOptionId === option.Id
                                                        )
                                                    ) ?? { value: '', label: 'No map' };
                                                    let hasNoMap = valueAttribute.value == '';
                                                    let hasDuplicateMap =
                                                        !hasNoMap &&
                                                        (
                                                            mappingAttributeForSC?.Options?.filter(
                                                                (mappOption) => mappOption.MerchantProductOptionId === valueAttribute.value
                                                            ) ?? []
                                                        ).length > 1;

                                                    return (
                                                        mappingAttributeForSC && (
                                                            <FormControl
                                                                key={'OptionComboSC_' + indexOptionSC}
                                                                className={
                                                                    indexOptionSC < maxIndexColumnVisible && indexOptionSC >= minIndexColumnVisible
                                                                        ? 'ctrlVisible'
                                                                        : 'ctrlHidden'
                                                                }
                                                            >
                                                                <AdvancedSelect
                                                                    className={
                                                                        !forEditProduct &&
                                                                        isProductTemplate &&
                                                                        hasButtonPublishClicked === true &&
                                                                        (hasNoMap || hasDuplicateMap)
                                                                            ? 'errorOption'
                                                                            : ''
                                                                    }
                                                                    isSearchable={false}
                                                                    options={optionsCombo}
                                                                    menuPosition={'fixed'}
                                                                    value={valueAttribute}
                                                                    onChange={(item) => onChangeOption(item, ma, option, msc.SalesChannelID)}
                                                                />
                                                                {!forEditProduct && isProductTemplate && hasButtonPublishClicked === true && hasNoMap && (
                                                                    <LabelErrorMessageOption>
                                                                        {T._("Please select a matching template's attribute", 'Admin')}
                                                                    </LabelErrorMessageOption>
                                                                )}
                                                                {!forEditProduct &&
                                                                    isProductTemplate &&
                                                                    hasButtonPublishClicked === true &&
                                                                    hasDuplicateMap && (
                                                                        <LabelErrorMessageOption>
                                                                            {T._('Attribute already assigned', 'Admin')}
                                                                        </LabelErrorMessageOption>
                                                                    )}
                                                            </FormControl>
                                                        )
                                                    );
                                                })}
                                            </MappingColumns>
                                        );
                                    })}
                                    <AttributeSeparator isLastSeparator={indexAttribute == modelAttributes.length - 1}></AttributeSeparator>
                                </React.Fragment>
                            );
                        })}
                    </MappingTableContainer>
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

const HeaderColumnMapping: FunctionComponent<{
    numSalesChannel: number;
    showTooltipOnPrimaryText: boolean;
    primaryText: string;
    secondaryText?: string;
    hasNoVariants?: boolean;
}> = ({ numSalesChannel, showTooltipOnPrimaryText, primaryText, secondaryText, hasNoVariants }) => {
    return (
        <HeaderColumnMappingContainer>
            {showTooltipOnPrimaryText ? (
                <ZTooltip position={'top'} title={primaryText}>
                    <HeaderColumn numSalesChannels={numSalesChannel}>{primaryText}</HeaderColumn>
                </ZTooltip>
            ) : (
                <HeaderColumn numSalesChannels={numSalesChannel}>{primaryText}</HeaderColumn>
            )}
            {secondaryText && <HeaderSecondaryColumn>{secondaryText}</HeaderSecondaryColumn>}
            {hasNoVariants && (
                <NoVariantsWarning>
                    <InfoCircleIcon />
                    <span>{T._('There seems to be an issue with the variants. Please verify them directly.', 'Admin')}</span>
                </NoVariantsWarning>
            )}
        </HeaderColumnMappingContainer>
    );
};

export default ConnectProductCatalogVariants;
